// Which engine modules this build implements (design sections 7.1 and 8.4), as plain flags, and
// the one availability rule every parser of CONDITIONS_SPORT_REVISIONS applies. Pure data with no
// engine imports, so lib/conditions/sport-scoring-versions.ts (imported almost everywhere) parses
// the flag for readers with exactly the rule the writers use, without loading any kernel.
//
// Readers and writers must agree on fail-closed: a publish revision this build cannot produce
// invalidates its family for BOTH, so readers keep revision 1 while writers stop that family's
// legacy writes. Before this manifest, readers checked only that a revision was declared, so
// `surf=2` on a build without the surf revision 2 kernel moved readers to revision 2 while every
// row still carried revision 1.
//
// Implemented is not wired. FAMILY_ENGINE_IMPLEMENTED says a family's engine module exists;
// FAMILY_ENGINE_WIRED says the route's writers for that family run the engine and stamp its
// revisions (WP5). A publish revision other than 1, or any shadow target, needs both: before the
// wiring the legacy writers keep stamping revision 1, so readers keyed on revision 3 (or 2) would
// read every row as stale, and a shadow would name targets nothing computes.
//
// Ownership: WP0 owns this file, but each flag is flipped by the package that replaces the
// matching stub, in the same change (a one-line edit): FAMILY_ENGINE_IMPLEMENTED by WP1 (coastal),
// WP2 (sailing) and WP3 (mtb); REVISION_TWO_KERNEL_IMPLEMENTED by the WP-S package that owns the
// sport; FAMILY_ENGINE_WIRED by WP5, in the change that wires the family's writers (from the
// Phase 2b integration on, WP5 owns those three flags). core/__tests__/availability.test.ts and
// registryProblems() (so also scripts/check-sport-revisions.ts) fail while an implementation flag
// and its module disagree; the wiring flags have no module to compare with.
import { LEGACY_REVISION, REVISION_TWO, formulaRevision, revisionDeclaration } from "./revisions";
import { ENGINE_FAMILY_BY_SPORT } from "./sports";
import type { EngineFamily, EngineSport } from "./types";

/**
 * Whether this build can produce `revision` of `sport` for the given use. A publish revision 1
 * is always producible (the legacy code writes it); shadow targets and revisions above 1 need an
 * engine kernel.
 */
export type RevisionAvailability = (sport: EngineSport, revision: number, use: "publish" | "shadow") => boolean;

/** What a build implements (the family engines and the revision 2 kernels) and what the route wires. */
export type EngineImplementation = Readonly<{
  familyEngine: (family: EngineFamily) => boolean;
  /** The route's writers run the family's engine and stamp its revisions (FAMILY_ENGINE_WIRED). */
  familyWired: (family: EngineFamily) => boolean;
  revisionTwoKernel: (sport: EngineSport) => boolean;
}>;

/** engines/<family>/index.ts exports a family engine (not the WP0 null stub). */
export const FAMILY_ENGINE_IMPLEMENTED: Readonly<Record<EngineFamily, boolean>> = Object.freeze({
  coastal: true,
  sailing: true,
  mtb: true,
});

/**
 * The route writes engine revisions for the family: WP5 flips each flag in the change that wires
 * the family's writers (MTB with the ski slice). Until then a publish revision other than 1 and
 * every shadow target are unavailable, whatever the modules implement, because the legacy writers
 * keep stamping revision 1. Owned by WP5 from the Phase 2b integration on. Coastal is wired
 * (P2C-WP5-05 and -08: the surf slice runs the coastal engine and serves every sport publishing a
 * revision other than 1 through the carriers, app/api/conditions/coastal-engine.ts), so is
 * sailing (P2C-WP5-05 and -09: the sailing slice does the same through the sailing carriers,
 * app/api/conditions/sailing-engine.ts), and so is MTB with the ski slice (P2C-WP5-05 and -10:
 * the ski slice runs the MTB engine and serves MTB through the MTB carriers when it publishes a
 * revision other than 1, app/api/conditions/mtb-engine.ts).
 */
export const FAMILY_ENGINE_WIRED: Readonly<Record<EngineFamily, boolean>> = Object.freeze({
  coastal: true,
  sailing: true,
  mtb: true,
});

/** engines/<family>/r2/<sport>.ts exports a revision 2 kernel (not the WP0 null stub). */
export const REVISION_TWO_KERNEL_IMPLEMENTED: Readonly<Record<EngineSport, boolean>> = Object.freeze({
  surf: true,
  bodyboarding: true,
  wind: true,
  foil: true,
  "downwind-foil": true,
  "wing-foil": true,
  diving: true,
  swim: true,
  sup: true,
  "sup-touring": true,
  "sup-surf": true,
  "sup-downwind": true,
  "sailing-club": true,
  "sailing-hp": true,
  mtb: true,
});

/**
 * Whether the modules can run `revision` of `sport` (the wiring aside). Publishing revision 1
 * needs nothing (the legacy code writes it); every engine-run target needs the family engine, and
 * a kernel revision needs its module as well. An alias is implemented when its formula revision
 * is. Undeclared revisions never are. registryProblemsFor checks the kernels of every revision
 * this says the modules implement, whether or not the family is wired.
 */
export function revisionImplementedFor(implementation: EngineImplementation): RevisionAvailability {
  return (sport, revision, use) => {
    const base = formulaRevision(sport, revision);
    if (base === null) return false;
    if (revision === LEGACY_REVISION && use === "publish") return true;
    if (!implementation.familyEngine(ENGINE_FAMILY_BY_SPORT[sport])) return false;
    const declaration = revisionDeclaration(sport, base);
    if (!declaration) return false;
    if (declaration.kind === "legacy") return true;
    return base === REVISION_TWO ? implementation.revisionTwoKernel(sport) : false;
  };
}

/**
 * The availability rule every parser of the revision flags applies. Publishing revision 1 needs
 * nothing; any other publish revision and every shadow target need the modules
 * (revisionImplementedFor) and the family's wired writers (familyWired): before WP5 wires a
 * family, `surf=3` or `sailing-club=2` fails that family closed for readers and writers alike,
 * as it did before the modules landed.
 */
export function revisionAvailableFor(implementation: EngineImplementation): RevisionAvailability {
  const implemented = revisionImplementedFor(implementation);
  return (sport, revision, use) => {
    if (!implemented(sport, revision, use)) return false;
    if (revision === LEGACY_REVISION && use === "publish") return true;
    return implementation.familyWired(ENGINE_FAMILY_BY_SPORT[sport]);
  };
}

/** The implementation this manifest declares. */
export const MANIFEST_IMPLEMENTATION: EngineImplementation = Object.freeze({
  familyEngine: (family: EngineFamily) => FAMILY_ENGINE_IMPLEMENTED[family],
  familyWired: (family: EngineFamily) => FAMILY_ENGINE_WIRED[family],
  revisionTwoKernel: (sport: EngineSport) => REVISION_TWO_KERNEL_IMPLEMENTED[sport],
});

/**
 * Availability from the manifest: identical to core/registry.ts revisionAvailable (a test pins
 * the equality for every sport, revision and use), without importing any engine module.
 */
export const manifestRevisionAvailable: RevisionAvailability = revisionAvailableFor(MANIFEST_IMPLEMENTATION);
